"use client";

import { useBuyerPropertiesStore, type BuyerUnit } from "@/providers/global/stores/buyer-properties-store";
import { type BuyerProject } from "@/types/buyerProject";
import { useEffect, useState } from "react";
import { useAuth } from "./use-auth";
import { useBuyerDashboard } from "./use-buyer-dashboard";

/**
 * Global hook to initialize buyer properties store after authentication
 * This should be called at a high level (like in the main layout or after auth provider)
 * to ensure buyer properties data is available across the entire app
 */
export function useGlobalBuyerProperties() {
	const { isAuthenticated } = useAuth();
	const { setBuyerUnits, setBuyerProjects } = useBuyerPropertiesStore();
	const { rawBuyerUnits, rawBuyerProjects, buyerGroupId } = useBuyerDashboard();
	const [isHydrated, setIsHydrated] = useState(false);

	// Mark as hydrated after initial render
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	useEffect(() => {
		// Only initialize after hydration to avoid blocking initial render
		if (isHydrated && isAuthenticated && buyerGroupId) {
			// Use requestIdleCallback to defer heavy operations
			const initializeBuyerProperties = () => {
				// Set units if we have them
				if (rawBuyerUnits?.length > 0) {
					setBuyerUnits(rawBuyerUnits as BuyerUnit[], buyerGroupId);
				}

				// Set projects if we have them
				if (rawBuyerProjects?.length > 0) {
					setBuyerProjects(rawBuyerProjects as BuyerProject[], buyerGroupId);
				}
			};

			if (window.requestIdleCallback) {
				window.requestIdleCallback(initializeBuyerProperties, { timeout: 1000 });
			} else {
				setTimeout(initializeBuyerProperties, 0);
			}
		}
	}, [isHydrated, isAuthenticated, buyerGroupId, rawBuyerUnits, rawBuyerProjects, setBuyerUnits, setBuyerProjects]);

	return {
		isInitialized:
			isHydrated && isAuthenticated && buyerGroupId && (rawBuyerUnits?.length > 0 || rawBuyerProjects?.length > 0),
	};
}
